import { action, computed, observable } from 'mobx';

import {
    defaultRegistroImpuestoPredial,
    impuestosPredialDesgloseConceptosByKey,
    impuestosPredialDesgloseKeys
} from './constants';
import { registroImpuestosPredialStore } from './registroImpuestosPredialStore';
import { setValueByPath, sum } from './utils';

import {
    ImpuestosDesgloseRecord,
    ImpuestosDesgloseTotalRecord,
    ImpuestosPredialDesglose
} from '../models';

export class ImpuestosPredialDesgloseStore {
    kind: 'ingresos' | 'descuentos';

    @observable
    isSaving = false;

    constructor(kind: 'ingresos' | 'descuentos') {
        this.kind = kind;
    }

    @computed
    get isLoading() {
        return registroImpuestosPredialStore.isLoading;
    }

    @computed
    get ejercicio(): number {
        return registroImpuestosPredialStore.apertura.ejercicio;
    }

    @computed
    get desgloseEjercicioRecords(): ImpuestosDesgloseRecord[] {
        return this.resolveDesgloseRecords(this.getDesgloseEjercicio());
    }

    @computed
    get desgloseEjercicioAnteriorRecords(): ImpuestosDesgloseRecord[] {
        return this.resolveDesgloseRecords(this.getDesgloseEjercicioAnterior());
    }

    @computed
    get desgloseTotalRecords(): ImpuestosDesgloseTotalRecord[] {
        return this.resolveDesgloseTotalRecords();
    }

    @action
    setDesgloseEjercicioValue = (path: string, value: number) => {
        setValueByPath(this.getDesgloseEjercicio(), path, value);
        registroImpuestosPredialStore.markDirty();
    };

    @action
    setDesgloseEjercicioAnteriorValue = (path: string, value: number) => {
        setValueByPath(this.getDesgloseEjercicioAnterior(), path, value);
        registroImpuestosPredialStore.markDirty();
    };

    @action
    saveDesglose = async () => {
        this.isSaving = true;
        const { registroImpuestos } = registroImpuestosPredialStore;
        const { estado, id } = registroImpuestos;
        const subform = registroImpuestos[this.kind];
        const formatoNum = this.kind === 'ingresos' ? 1 : 3;

        if (estado === 'Sin Captura' || id === 0) {
            await registroImpuestosPredialStore.getNewForm();
        }

        const result = await registroImpuestosPredialStore.saveForm(subform, formatoNum);
        registroImpuestosPredialStore.unmarkDirty();

        this.isSaving = false;
        if (result === 'success') {
            registroImpuestosPredialStore.registroImpuestos[this.kind].isSaved = true;
        }
    };

    private getDesgloseEjercicio = (): ImpuestosPredialDesglose =>
        this.kind === 'ingresos'
            ? registroImpuestosPredialStore[this.kind].ingresosEjercicio
            : registroImpuestosPredialStore[this.kind].descuentosEjercicio;

    private getDesgloseEjercicioAnterior = (): ImpuestosPredialDesglose =>
        this.kind === 'ingresos'
            ? registroImpuestosPredialStore[this.kind].ingresosEjercicioAnterior
            : registroImpuestosPredialStore[this.kind].descuentosEjercicioAnterior;

    private resolveDesgloseRecords = (
        impuestosPredialDesgloseImporteByKey: ImpuestosPredialDesglose
    ): ImpuestosDesgloseRecord[] =>
        impuestosPredialDesgloseKeys.map(key => ({
            key,
            concepto: impuestosPredialDesgloseConceptosByKey[key],
            importe: impuestosPredialDesgloseImporteByKey[key]
        }));

    private resolveDesgloseTotalRecords = (): ImpuestosDesgloseTotalRecord[] => [
        {
            key: 'total',
            concepto: 'TOTAL',
            importe:
                Object.values(this.getDesgloseEjercicio()).reduce(sum) +
                Object.values(this.getDesgloseEjercicioAnterior()).reduce(sum)
        }
    ];
}

export const impuestosPredialIngresosStore = new ImpuestosPredialDesgloseStore('ingresos');
export const impuestosPredialDescuentosStore = new ImpuestosPredialDesgloseStore('descuentos');
